import { createContext, useContext, useState, useMemo } from 'react';
import { UserApiModel, WithDomains, WithRoles } from '../../../types/models/user.model';
import { Permission } from '../../../types/constants/permission';
import { RoleApiModel } from '../../../types/models/role.model';
import { DomainApiModel } from '../../../types/models/domain.model';

export class SessionUser implements UserApiModel, WithRoles, WithDomains {
  public readonly id: number;
  public readonly firstName: string;
  public readonly lastName: string;
  public readonly email: string;
  public readonly roles: RoleApiModel[];
  public readonly domains: DomainApiModel[];

  constructor(user: UserApiModel & WithRoles & WithDomains) {
    this.id = user.id;
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.email = user.email;
    this.roles = user.roles;
    this.domains = user.domains;
  }

  public hasPermission(permission: Permission) {
    return this.roles.find(({ permissions }) => permissions?.includes(permission));
  }
}

type UserContextState = [SessionUser | undefined, React.Dispatch<React.SetStateAction<SessionUser | undefined>>] | null;

const userContext = createContext<UserContextState>(null);

export type UserProviderProps = { user?: SessionUser; children: React.ReactNode };

const useUser = () => {
  const context = useContext(userContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

const UserProvider = ({ user: initialUser, children }: UserProviderProps) => {
  const [user, setUser] = useState<SessionUser | undefined>(initialUser);

  const value = useMemo<[SessionUser | undefined, React.Dispatch<React.SetStateAction<SessionUser | undefined>>]>(
    () => [user, setUser],
    [user],
  );

  return <userContext.Provider value={value}>{children}</userContext.Provider>;
};

export { UserProvider, useUser };
